import { initializeTextDecalEditor } from './textDecalEditor.js';
import { applyTattooLook2D, generateTattooPreview } from './utils/tattooLook.js';

export function setupEventListeners(texturePainter) {
    
    if (typeof window.hotkeysPaused === 'undefined') {
        window.hotkeysPaused = false;
    }
    window.addEventListener('resize', () => texturePainter.onWindowResize());

    document.getElementById('modelInput').addEventListener('change', (e) => texturePainter.loadModel(e.target.files[0]));
    document.getElementById('loadDefaultModel').addEventListener('click', () => texturePainter.loadDefaultModel());

    document.getElementById('decalInput').addEventListener('change', (e) => texturePainter.loadDecalTexture(e.target.files[0]));

    document.getElementById('compactMode2D').addEventListener('click', () => {
        texturePainter.setProjectionMode('2d');
        texturePainter.updateModeButtons('2d');
    });

    document.getElementById('compactMode3D').addEventListener('click', () => {
        texturePainter.setProjectionMode('3d');
        texturePainter.updateModeButtons('3d');
    });

    document.getElementById('compactModeBrush').addEventListener('click', () => {
        texturePainter.setProjectionMode('brush');
        texturePainter.updateModeButtons('brush');
    });

    const projectionModeSelector = document.getElementById('projectionModeSelector');
    if (projectionModeSelector) {
        projectionModeSelector.addEventListener('change', (e) => {
            texturePainter.setProjectionMode(e.target.value);
            texturePainter.updateModeButtons(e.target.value);
        });
    }

    document.getElementById('opacitySlider').addEventListener('input', (e) => {
        const value = e.target.value;
        document.getElementById('opacityValue').textContent = value + '%';
        const opacity = value / 100;
        
        if (texturePainter.projectionMode === '2d') {
            texturePainter.updateDecalOpacity(opacity);
        } else {
            texturePainter.set3DProjectionOpacity(opacity);
        }
    });

    document.getElementById('scaleSlider').addEventListener('input', (e) => {
        const value = e.target.value;
        document.getElementById('scaleValue').textContent = value;
        
        if (texturePainter.projectionMode === '2d') {
            texturePainter.updateDecalScale(parseFloat(value));
        } else {
            texturePainter.set3DProjectorSize(parseFloat(value));
        }
    });

    document.getElementById('resolutionSelector').addEventListener('change', (e) => {
        const resolution = e.target.value;
        document.getElementById('resolutionValue').textContent = `${resolution}x${resolution}`;
        texturePainter.updateTextureResolution(parseInt(resolution));
    });

    document.getElementById('clearDecals').addEventListener('click', () => {
        const confirmed = confirm('This will clear ALL decals and reset ALL UDIM textures across every channel. This action cannot be undone. Do you want to proceed?');
        if (confirmed) {
            texturePainter.clearDecals();
        }
    });

    document.getElementById('applyDecal').addEventListener('click', () => {
        if (texturePainter.projectionMode === '2d') {
            texturePainter.pushUndoCheckpoint?.();
            texturePainter.applyMoveableDecal();
        } else if (texturePainter.projectionMode === '3d') {
            texturePainter.pushUndoCheckpoint?.();
            texturePainter.apply3DProjection();
        }
    });
    document.getElementById('cancelDecal').addEventListener('click', () => {
        if (texturePainter.projectionMode === '2d') {
            texturePainter.cancelMoveableDecal();
        } else if (texturePainter.projectionMode === '3d') {
            texturePainter.cancel3DProjection();
        }
    });

    const translateBtn = document.getElementById('translateMode');
    const rotateBtn = document.getElementById('rotateMode');
    const scaleBtn = document.getElementById('scaleMode');

    const setTransformUIActive = (mode) => {
        if (!translateBtn || !rotateBtn || !scaleBtn) return;
        translateBtn.classList.toggle('active', mode === 'translate');
        rotateBtn.classList.toggle('active', mode === 'rotate');
        scaleBtn.classList.toggle('active', mode === 'scale');
    };

    if (translateBtn) {
        translateBtn.addEventListener('click', () => {
            texturePainter.transformControls.setMode('translate');
            setTransformUIActive('translate');
        });
    }
    if (rotateBtn) {
        rotateBtn.addEventListener('click', () => {
            texturePainter.transformControls.setMode('rotate');
            setTransformUIActive('rotate');
        });
    }
    if (scaleBtn) {
        scaleBtn.addEventListener('click', () => {
            texturePainter.transformControls.setMode('scale');
            setTransformUIActive('scale');
        });
    }

    // Initialize transform UI state to current mode if available
    try {
        const currentMode = texturePainter.transformControls?.getMode?.();
        if (currentMode) {
            // slight delay to ensure DOM is ready/styles applied
            setTimeout(() => setTransformUIActive(currentMode), 0);
        }
    } catch {
        // ignore if transformControls not ready yet
    }

    // Temporary transform hotkeys while a 3D projector is placed:
    // Hold Ctrl -> Rotate, Hold Ctrl+Shift -> Scale. Release -> restore previous mode.
    // Only active when in 3D mode AND a projector is attached to transformControls.
    (() => {
        let prevTransformMode = null; // 'translate' | 'rotate' | 'scale' | null
        let isTempModeActive = false;

        const hasActive3DProjector = () => {
            // Active when projection mode is 3d and transformControls has an attached object (the projector)
            return texturePainter.projectionMode === '3d' &&
                   !!texturePainter.transformControls?.object &&
                   !!texturePainter.projective3D?.projector;
        };

        const applyTempModeIfNeeded = (e) => {
            if (!hasActive3DProjector()) return;

            // Prefer userAgentData when available; fallback to platform
            const uaIsMac = navigator.userAgentData
                ? ((navigator.userAgentData.platform || '').toLowerCase().includes('mac'))
                : ((navigator.platform || '').toLowerCase().includes('mac'));
            const ctrlOrCmd = uaIsMac ? e.metaKey : e.ctrlKey;

            const wantScale = ctrlOrCmd && e.shiftKey;
            const wantRotate = ctrlOrCmd && !e.shiftKey;

            if (wantScale || wantRotate) {
                const current = texturePainter.transformControls?.getMode?.();
                if (!isTempModeActive) {
                    // store once when entering temp
                    prevTransformMode = current || 'translate';
                    isTempModeActive = true;
                }
                const desired = wantScale ? 'scale' : 'rotate';
                if (current !== desired) {
                    texturePainter.transformControls.setMode(desired);
                    // reflect in UI buttons if present
                    try {
                        const translateBtn = document.getElementById('translateMode');
                        const rotateBtn = document.getElementById('rotateMode');
                        const scaleBtn = document.getElementById('scaleMode');
                        if (translateBtn && rotateBtn && scaleBtn) {
                            translateBtn.classList.toggle('active', desired === 'translate');
                            rotateBtn.classList.toggle('active', desired === 'rotate');
                            scaleBtn.classList.toggle('active', desired === 'scale');
                        }
                    } catch {}
                    texturePainter.updateStatus(desired[0].toUpperCase() + desired.slice(1) + ' mode (temporary)');
                }
            } else if (isTempModeActive) {
                // No longer holding modifiers -> restore previous
                if (prevTransformMode && texturePainter.transformControls) {
                    texturePainter.transformControls.setMode(prevTransformMode);
                    try {
                        const translateBtn = document.getElementById('translateMode');
                        const rotateBtn = document.getElementById('rotateMode');
                        const scaleBtn = document.getElementById('scaleMode');
                        if (translateBtn && rotateBtn && scaleBtn) {
                            translateBtn.classList.toggle('active', prevTransformMode === 'translate');
                            rotateBtn.classList.toggle('active', prevTransformMode === 'rotate');
                            scaleBtn.classList.toggle('active', prevTransformMode === 'scale');
                        }
                    } catch {}
                    texturePainter.updateStatus(prevTransformMode[0].toUpperCase() + prevTransformMode.slice(1) + ' mode restored');
                }
                isTempModeActive = false;
                prevTransformMode = null;
            }
        };

        // Track keydown to switch into temp modes
        document.addEventListener('keydown', (e) => {
            const paused = typeof window.hotkeysPaused !== 'undefined' ? window.hotkeysPaused : false;

            // Prefer userAgentData when available; fallback to platform
            const uaIsMac = navigator.userAgentData
                ? ((navigator.userAgentData.platform || '').toLowerCase().includes('mac'))
                : ((navigator.platform || '').toLowerCase().includes('mac'));
            const ctrlOrCmd = uaIsMac ? e.metaKey : e.ctrlKey;

            if (ctrlOrCmd && (e.key === 'z' || e.key === 'Z')) {
                if (e.repeat) return;
                texturePainter.undo?.();
                return;
            }

            if (!paused) {
                // First allow built-in single-key hotkeys (W/E/R/1/2/3/Escape/Enter)
                texturePainter.onKeyDown(e);
                // Then handle temporary transform override
                applyTempModeIfNeeded(e);
            }
        });

        // Also listen to keyup to restore when modifiers released
        document.addEventListener('keyup', (e) => {
            if (typeof window.hotkeysPaused !== 'undefined' ? window.hotkeysPaused : false) return;
            applyTempModeIfNeeded(e);
        });

        // Safety: if projector is removed while temp active, restore immediately
        const restoreIfProjectorRemoved = () => {
            if (isTempModeActive && !hasActive3DProjector()) {
                if (prevTransformMode && texturePainter.transformControls) {
                    texturePainter.transformControls.setMode(prevTransformMode);
                    try {
                        const translateBtn = document.getElementById('translateMode');
                        const rotateBtn = document.getElementById('rotateMode');
                        const scaleBtn = document.getElementById('scaleMode');
                        if (translateBtn && rotateBtn && scaleBtn) {
                            translateBtn.classList.toggle('active', prevTransformMode === 'translate');
                            rotateBtn.classList.toggle('active', prevTransformMode === 'rotate');
                            scaleBtn.classList.toggle('active', prevTransformMode === 'scale');
                        }
                    } catch {}
                }
                isTempModeActive = false;
                prevTransformMode = null;
            }
        };

        // Observe projector detach via a small polling loop (cheap, only active while page alive)
        setInterval(restoreIfProjectorRemoved, 250);
    })();

    // Fallback keydown pipeline (non-transform related)
    document.addEventListener('keydown', (e) => {
        const paused = typeof window.hotkeysPaused !== 'undefined' ? window.hotkeysPaused : false;

        // Prefer userAgentData when available; fallback to platform
        const uaIsMac = navigator.userAgentData
            ? ((navigator.userAgentData.platform || '').toLowerCase().includes('mac'))
            : ((navigator.platform || '').toLowerCase().includes('mac'));
        const ctrlOrCmd = uaIsMac ? e.metaKey : e.ctrlKey;

        if (ctrlOrCmd && (e.key === 'z' || e.key === 'Z')) {
            if (e.repeat) return;
            texturePainter.undo?.();
            return;
        }

        if (!paused) {
            texturePainter.onKeyDown(e);
        }
    });

    document.getElementById('exportTextures').addEventListener('click', () => texturePainter.exportTextures());

    const applyTattooBtn = document.getElementById('applyTattooLook');
    const tattooModal = document.getElementById('tattooModal');
    const tattooClose = document.getElementById('tattooClose');
    const tattooCancel = document.getElementById('tattooCancel');
    const tattooApply = document.getElementById('tattooApply');

    const tattooOpacity = document.getElementById('tattooOpacity');
    const tattooOpacityVal = document.getElementById('tattooOpacityVal');
    const tattooBlendMix = document.getElementById('tattooBlendMix');
    const tattooBlendVal = document.getElementById('tattooBlendVal');
    const tattooEdgeBlur = document.getElementById('tattooEdgeBlur');
    const tattooBlurVal = document.getElementById('tattooBlurVal');
    const tattooNoise = document.getElementById('tattooNoise');
    const tattooNoiseVal = document.getElementById('tattooNoiseVal');
    const tattooBleed = document.getElementById('tattooBleed');
    const tattooBleedVal = document.getElementById('tattooBleedVal');

    const updateTattooPreview = () => {
        const previewCanvas = document.getElementById('tattooPreview');
        const placeholder = document.getElementById('tattooPreviewPlaceholder');
        if (!previewCanvas || !placeholder) return;

        const params = {
            opacity: tattooOpacity ? parseFloat(tattooOpacity.value) : 0.8,
            blendMix: tattooBlendMix ? parseFloat(tattooBlendMix.value) : 0.7,
            edgeBlur: tattooEdgeBlur ? parseFloat(tattooEdgeBlur.value) : 2.0,
            noise: tattooNoise ? parseFloat(tattooNoise.value) : 0.2,
            bleed: tattooBleed ? parseFloat(tattooBleed.value) : 0.05
        };

        const preview = generateTattooPreview(texturePainter, params);
        if (preview) {
            const ctx = previewCanvas.getContext('2d');
            ctx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
            ctx.drawImage(preview, 0, 0);
            previewCanvas.style.display = 'block';
            placeholder.style.display = 'none';
        } else {
            previewCanvas.style.display = 'none';
            placeholder.style.display = 'block';
        }
    };

    const syncTattooLabels = () => {
        if (tattooOpacity && tattooOpacityVal) tattooOpacityVal.textContent = parseFloat(tattooOpacity.value).toFixed(2);
        if (tattooBlendMix && tattooBlendVal) {
            const mix = parseFloat(tattooBlendMix.value);
            const multPct = Math.round(mix * 100);
            const softPct = 100 - multPct;
            tattooBlendVal.textContent = `${multPct}% / ${softPct}%`;
        }
        if (tattooEdgeBlur && tattooBlurVal) tattooBlurVal.textContent = tattooEdgeBlur.value;
        if (tattooNoise && tattooNoiseVal) tattooNoiseVal.textContent = parseFloat(tattooNoise.value).toFixed(2);
        if (tattooBleed && tattooBleedVal) tattooBleedVal.textContent = parseFloat(tattooBleed.value).toFixed(3);

        updateTattooPreview();
    };

    if (applyTattooBtn && tattooModal) {
        applyTattooBtn.addEventListener('click', () => {
            tattooModal.style.display = 'flex';
            window.hotkeysPaused = true;
            syncTattooLabels();
        });
    }
    if (tattooClose && tattooModal) {
        tattooClose.addEventListener('click', () => {
            tattooModal.style.display = 'none';
            window.hotkeysPaused = false;
        });
    }
    if (tattooCancel && tattooModal) {
        tattooCancel.addEventListener('click', () => {
            tattooModal.style.display = 'none';
            window.hotkeysPaused = false;
        });
    }
    if (tattooModal) {
        window.addEventListener('click', (e) => {
            if (e.target === tattooModal) {
                tattooModal.style.display = 'none';
                window.hotkeysPaused = false;
            }
        });
    }

    if (tattooOpacity) tattooOpacity.addEventListener('input', syncTattooLabels);
    if (tattooBlendMix) tattooBlendMix.addEventListener('input', syncTattooLabels);
    if (tattooEdgeBlur) tattooEdgeBlur.addEventListener('input', syncTattooLabels);
    if (tattooNoise) tattooNoise.addEventListener('input', syncTattooLabels);
    if (tattooBleed) tattooBleed.addEventListener('input', syncTattooLabels);

    if (tattooApply) {
        tattooApply.addEventListener('click', () => {
            const params = {
                opacity: tattooOpacity ? parseFloat(tattooOpacity.value) : 0.8,
                blendMix: tattooBlendMix ? parseFloat(tattooBlendMix.value) : 0.7,
                edgeBlur: tattooEdgeBlur ? parseFloat(tattooEdgeBlur.value) : 2.0,
                noise: tattooNoise ? parseFloat(tattooNoise.value) : 0.2,
                bleed: tattooBleed ? parseFloat(tattooBleed.value) : 0.05
            };

            const bar = document.getElementById('tattooApplyProgressBar');
            const text = document.getElementById('tattooApplyProgressText');
            const wrap = document.getElementById('tattooApplyProgressWrap');

            const setProgress = (p, msg) => {
                if (bar) bar.style.width = `${Math.max(0, Math.min(100, Math.round(p)))}%`;
                if (text) text.textContent = msg ?? `${Math.round(p)}%`;
            };

            if (wrap) wrap.style.opacity = '1';
            setProgress(0, 'Starting...');

            const tp = texturePainter;

            const list = (tp.uvChannels || []).map((ch, idx) => ({ ch, idx }));

            const total = list.length || 1;
            let completed = 0;

            const channelStep = 100 / total;

            const runChannel = (i) => {
                if (i >= list.length) {
                    setProgress(100, 'Done');
                    setTimeout(() => {
                        try {
                            tp.refreshCharacterMaterials?.();
                            tp.render?.();
                        } finally {
                            tattooModal.style.display = 'none';
                            window.hotkeysPaused = false;
                            setTimeout(() => {
                                if (wrap) wrap.style.opacity = '0.6';
                                if (text) text.textContent = 'Idle';
                                if (bar) bar.style.width = '0%';
                            }, 600);
                        }
                    }, 50);
                    return;
                }

                const originalChannels = tp.uvChannels;
                tp.uvChannels = [list[i].ch];
                try {
                    const basePct = Math.min(99, completed * channelStep);
                    setProgress(Math.max(2, basePct), `Processing ${completed + 1}/${total}...`);

            const doWork = () => {
                // Create undo checkpoint before mutating decal canvases with tattoo filter
                tp.pushUndoCheckpoint?.();
                        try {
                            const midPct = Math.min(99, basePct + channelStep * 0.5);
                            setProgress(midPct, `Processing ${completed + 1}/${total} (processing)...`);
                            applyTattooLook2D(tp, params);
                        } finally {
                            tp.uvChannels = originalChannels;
                            if (typeof tp.compositeTexture === 'function') {
                                tp.compositeTexture(list[i].idx);
                            }
                            completed++;
                            const endPct = Math.min(99, completed * channelStep);
                            setProgress(endPct, `Processed ${completed}/${total}`);
                            if (typeof requestAnimationFrame === 'function') {
                                requestAnimationFrame(() => runChannel(i + 1));
                            } else {
                                setTimeout(() => runChannel(i + 1), 0);
                            }
                        }
                    };

                    if (typeof requestAnimationFrame === 'function') {
                        requestAnimationFrame(doWork);
                    } else {
                        setTimeout(doWork, 0);
                    }
                } catch (e) {
                    console.error('Tattoo apply failed for channel', list[i].idx, e);
                    tp.uvChannels = originalChannels;
                    completed++;
                    if (typeof requestAnimationFrame === 'function') {
                        requestAnimationFrame(() => runChannel(i + 1));
                    } else {
                        setTimeout(() => runChannel(i + 1), 0);
                    }
                }
            };

            setProgress(1, 'Preparing...');
            if (typeof requestAnimationFrame === 'function') {
                requestAnimationFrame(() => runChannel(0));
            } else {
                setTimeout(() => runChannel(0), 0);
            }
        });
    }


    if (texturePainter.renderer?.domElement) {
        texturePainter.renderer.domElement.addEventListener('click', (e) => texturePainter.onMouseClick(e));
    }

    initializeTextDecalEditor(texturePainter);

    (() => {
        const preset = document.getElementById('baseSkinSelector');
        const picker = document.getElementById('baseSkinColorPicker');

        const applyBaseColorFromPicker = () => {
            if (picker && preset) {
                const match = Array.from(preset.options).find(
                    o => o.value.toLowerCase() === picker.value.toLowerCase()
                );
                if (match) preset.value = match.value;
            }

            texturePainter.updateBaseSkinColor();
        };

        if (preset) {
            preset.addEventListener('change', () => {
                if (picker) picker.value = preset.value;
                applyBaseColorFromPicker();
            });
        }

        if (picker) {
            const handler = () => applyBaseColorFromPicker();
            picker.addEventListener('input', handler);
            picker.addEventListener('change', handler);
        }
    })();

}
